import { apiClient } from '../../core/api';

// Types based on Angular implementation
export interface TrendModel {
  type: 'conversation' | 'lead' | 'visitor';
  thisPeriodNum: number;
  rate: string; // percentage like "15.3"
}

export interface DataModel {
  count: number;
  dateTime: string;
}

export interface DataStatisticModel {
  type: string;
  dimension: string;
  dataList?: DataModel[];
  totalNumber: number;
}

export interface ConversionModel {
  type: string;
  rate: number;
  thisPeriodNum: number;
  lastPeriodNum: number;
}

export interface DashboardModel {
  trendList: TrendModel[];
  dataStatisticList: DataStatisticModel[];
  conversionList?: ConversionModel[];
  errorCode?: number;
  errorMessage?: string;
}

export interface DashboardStat {
  title: string;
  value: string | number;
  changeRate: string | number;
  isPositiveChange: boolean;
  period?: string;
  icon?: string;
}

// Dashboard dimension values matching Angular implementation
// Note: API actually uses Overview format ('4 weeks', '12 weeks', etc.)
export type DashboardDimensionValue = '7 days' | '4 weeks' | '12 weeks' | '12 months' | 'today' | 'yesterday';

// API service class
class AnalyticsApiService {
  private baseURL: string;

  constructor() {
    // Use environment variable or fallback
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.example.com';
    this.baseURL = baseUrl.includes('/chatbot/api/v1') ? baseUrl : `${baseUrl}/chatbot/api/v1`;
  }

  /**
   * Get dashboard data including trends, statistics, and conversions
   * Based on Angular: getDashboardData(userId, dimension, endDateTime)
   */
  async getDashboardData(
    userId: string,
    dimension: DashboardDimensionValue = '7 days',
    endDateTime?: string
  ): Promise<DashboardModel> {
    try {
      // Generate endDateTime if not provided
      if (!endDateTime) {
        const now = new Date();
        endDateTime =
          now
            .toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
            .replace(/\//g, '-') +
          ' ' +
          now.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
      }

      const params: Record<string, string> = {
        dimension: dimension,
        endDateTime: endDateTime,
      };

      const response = await apiClient.get<DashboardModel>(`/dashboard/${userId}`, params);

      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      throw error;
    }
  }

  /**
   * Process trend data into dashboard stats format
   * Based on Angular: processTrendList and dashboardStats getter
   */
  processTrendListToStats(trendList: TrendModel[]): DashboardStat[] {
    const stats: DashboardStat[] = [];

    if (!trendList || !Array.isArray(trendList)) {
      return stats;
    }

    trendList.forEach(trend => {
      let title: string;
      const period = 'last week';

      switch (trend.type) {
        case 'conversation':
          title = 'Total Conversations';
          break;
        case 'lead':
          title = 'Total Leads';
          break;
        case 'visitor':
          title = 'Online Store Visitors';
          break;
        default:
          return; // Skip unknown types
      }

      const changeRate = parseFloat(trend.rate);
      const value = trend.thisPeriodNum ?? 0;

      stats.push({
        title,
        value: value.toLocaleString(),
        changeRate: trend.rate,
        isPositiveChange: changeRate >= 0,
        period,
      });
    });

    return stats;
  }

  /**
   * Get simple analytics summary for bottom stats cards
   * Extracts key metrics from dashboard data
   */
  getBottomStatsFromDashboard(
    dashboardData: DashboardModel
  ): Array<{ title: string; value: string | number }> {
    const stats: Array<{ title: string; value: string | number }> = [];

    // Find specific metrics from trend list
    const conversationTrend = dashboardData.trendList?.find(t => t.type === 'conversation');
    const leadTrend = dashboardData.trendList?.find(t => t.type === 'lead');
    const visitorTrend = dashboardData.trendList?.find(t => t.type === 'visitor');

    // Add visitors stat
    if (visitorTrend) {
      stats.push({
        title: 'Visitors',
        value: visitorTrend.thisPeriodNum.toLocaleString(),
      });
    }

    // Add chats/conversations stat
    if (conversationTrend) {
      stats.push({
        title: 'Chats',
        value: conversationTrend.thisPeriodNum.toLocaleString(),
      });
    }

    // Add leads stat (could be count or conversion rate)
    if (leadTrend) {
      stats.push({
        title: 'Leads',
        value: leadTrend.thisPeriodNum.toLocaleString(),
      });
    }

    // If we don't have enough stats, add placeholders or calculate from other data
    while (stats.length < 3) {
      // Try to extract from dataStatisticList or conversionList
      const conversionData = dashboardData.conversionList?.[0];
      if (conversionData && stats.length === 2) {
        stats.push({
          title: 'Conversion Rate',
          value: `${conversionData.rate.toFixed(2)}%`,
        });
      } else {
        // Add placeholder
        stats.push({
          title: stats.length === 0 ? 'Visitors' : stats.length === 1 ? 'Chats' : 'Leads',
          value: '0',
        });
      }
    }

    return stats.slice(0, 3); // Ensure we only return 3 stats
  }

  /**
   * Mock implementation for development
   * Simulates the actual API response structure
   */
  async getMockDashboardData(_userId: string): Promise<DashboardModel> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));

    return {
      trendList: [
        {
          type: 'conversation',
          thisPeriodNum: 48,
          rate: '-40.0',
        },
        {
          type: 'lead',
          thisPeriodNum: 7,
          rate: '-86.3',
        },
        {
          type: 'visitor',
          thisPeriodNum: 13,
          rate: '-85.7',
        },
      ],
      dataStatisticList: [
        {
          type: 'conversation',
          dimension: '7 days',
          dataList: [
            {
              count: 2,
              dateTime: '28-07-2025 15:59:59',
            },
            {
              count: 4,
              dateTime: '29-07-2025 15:59:59',
            },
            {
              count: 0,
              dateTime: '30-07-2025 15:59:59',
            },
            {
              count: 15,
              dateTime: '31-07-2025 15:59:59',
            },
            {
              count: 27,
              dateTime: '01-08-2025 15:59:59',
            },
            {
              count: 0,
              dateTime: '02-08-2025 15:59:59',
            },
            {
              count: 0,
              dateTime: '03-08-2025 15:59:59',
            },
          ],
          totalNumber: 48,
        },
        {
          type: 'lead',
          dimension: '7 days',
          dataList: [
            {
              count: 2,
              dateTime: '28-07-2025 15:59:59',
            },
            {
              count: 0,
              dateTime: '29-07-2025 15:59:59',
            },
            {
              count: 1,
              dateTime: '30-07-2025 15:59:59',
            },
            {
              count: 3,
              dateTime: '31-07-2025 15:59:59',
            },
            {
              count: 1,
              dateTime: '01-08-2025 15:59:59',
            },
            {
              count: 0,
              dateTime: '02-08-2025 15:59:59',
            },
            {
              count: 0,
              dateTime: '03-08-2025 15:59:59',
            },
          ],
          totalNumber: 7,
        },
        {
          type: 'visitor',
          dimension: '7 days',
          totalNumber: 13,
        },
      ],
    };
  }
}

// Export singleton instance
export const analyticsApi = new AnalyticsApiService();

// Export individual functions for easier testing
export const {
  getDashboardData,
  processTrendListToStats,
  getBottomStatsFromDashboard,
  getMockDashboardData,
} = analyticsApi;
